import { AttendanceCrewLogResponse } from '../../../api/scheduler/models/attendance-crew-log-response';
import { AttendanceCrewLogRowDto } from '../../../api/scheduler/models/attendance-crew-log-row-dto';
import { ExportAttendanceCrewLog$Params } from '../../../api/scheduler/fn/attendance-audit-log/export-attendance-crew-log';
import { GetAttendanceCrewLog$Params } from '../../../api/scheduler/fn/attendance-audit-log/get-attendance-crew-log';
import { WING_LABELS } from '../attendance-log.constants';
import { CrewLogHeader, CrewLogQuery, CrewLogResult, CrewLogRow } from '../models';
import { toShiftParams } from './attendance-shift-filter.util';
import { formatAverage } from './crew-log-summary.util';

/** An empty array would send no values anyway; leaving it out keeps the URL clean. */
function nonEmpty<T>(values: T[]): T[] | undefined {
  return values.length ? values : undefined;
}

/** Range, filter, search and sort parameters shared by the crew log and its export. */
export function toCrewLogExportParams(
  query: CrewLogQuery
): Omit<ExportAttendanceCrewLog$Params, 'timeZone'> {
  const { filters } = query;
  const search = query.search.trim();
  return {
    crewId: query.crewId,
    from: query.from,
    to: query.to,
    search: search || undefined,
    wingType: nonEmpty(filters.wings),
    ...toShiftParams(filters.shifts),
    clockInStatus: nonEmpty(filters.clockIn),
    clockOutStatus: nonEmpty(filters.clockOut),
    sort: `${query.sortKey},${query.sortDir}`,
  };
}

export function toCrewLogParams(
  query: CrewLogQuery
): Omit<GetAttendanceCrewLog$Params, 'timeZone'> {
  return { ...toCrewLogExportParams(query), page: query.page, size: query.size };
}

function toCrewLogRow(dto: AttendanceCrewLogRowDto): CrewLogRow {
  return {
    date: dto.date,
    base: dto.base ?? null,
    wing: dto.wingType,
    rotation: dto.rotation,
    shiftName: dto.shift?.name ?? null,
    clockInAt: dto.clockInAt,
    clockInStatus: dto.clockInStatus,
    delayInSeconds: dto.delayInSeconds,
    clockOutAt: dto.clockOutAt,
    clockOutStatus: dto.clockOutStatus,
    delayOutSeconds: dto.delayOutSeconds,
  };
}

/** Throws when the response carries no data, which the effect reports as a load failure. */
export function toCrewLogResult(response: AttendanceCrewLogResponse): CrewLogResult {
  const data = response.data;
  if (!data) {
    throw new Error('Crew log response had no data');
  }
  return {
    header: {
      crewId: data.crewId,
      employeeCode: data.crewIdDisplay,
      name: data.name,
      wing: data.wingType,
      rotation: data.logs[0]?.rotation ?? null,
    },
    summary: {
      shiftsLogged: data.summary.shiftsLogged,
      lateArrivals: data.summary.lateArrivals,
      avgDelayInLabel: formatAverage(data.summary.avgDelayInSeconds),
      avgWorkLabel: formatAverage(data.summary.avgWorkSeconds),
    },
    rows: data.logs.map(toCrewLogRow),
    totalElements: response.page?.totalElements ?? data.logs.length,
    totalPages: response.page?.totalPages ?? 1,
  };
}

/** `020 · Rotor Wing`. */
export function crewLogHeadline(header: CrewLogHeader): string {
  return [header.employeeCode, WING_LABELS[header.wing]]
    .filter((part): part is string => !!part)
    .join(' · ');
}
